// lib
import React, { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import ADND from '@aytm/lib/adnd/ADND'
import SchemaRules from '@aytm/app/stats/lib/schema-rules'

import { DataContext } from '@aytm/app/stats/page/question-views/vq/Component'

import { usePrevious } from '@aytm/app/insights/dashboard/components/hooks'
import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/helpers'
import { getLogicData, getRefs, getClustersRefs } from '@aytm/app/stats/lib/refs'
import { convertValuesToTree, convertTreeToValues } from '@aytm/app/stats/lib/converter'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { MultiStateMultiSelect } from 'primereact/multistatemultiselect'

import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import AdvancedFilterContainer from '@aytm/app/stats/page/components/AdvancedFilterContainer'

import ModeToggle from '@aytm/app/stats/page/question-views/vq/ModeToggle'
import AnswerVariantOptions from '@aytm/app/stats/page/question-views/vq/AnswerVariantOptions'

import { itemTemplate, valueTemplate } from '@aytm/app/stats/page/components/lib/templates'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/vq/styles/Component.module.scss'

const AnswerVariantOEX = ({ item, position, dndItemsList }) => {
  const {
    id,
    data,
    disabled,
    dispatch,
    dropdownsRef,
    parserResultPatternBasicsRef,
    basicFilters,
    parserResultEof,
    answerVariantAmount
  } = useContext(DataContext)

  const [processing, setProcessing] = useState(false)

  const [selectedValue, setSelectedValue] = useState(null)
  const [selectedValues, setSelectedValues] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)

  const [checkPattern, setCheckPattern] = useState('advanced' != item.mode || false != item.check)

  const changedRef = useRef(0 < item.pattern.trim().length)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const itemMode = useMemo(
    () =>
      disabled || 'advanced' == item.mode || !data.options.some(group => 0 < group.items.length) ? 'advanced' : 'basic',
    [item.mode, data.options, disabled]
  )

  const itemModeRef = useRef(null)
  itemModeRef.current = itemMode

  const parserResultPattern = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: item.pattern.trim(),
        clusters: data.clusters,
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.pattern, id, data.surveySchema, data.clusters]
  )
  const parserResultPatternPrev = usePrevious(parserResultPattern)

  const parserResultPatternError = useMemo(
    () =>
      0 < item.pattern.trim().length &&
      0 < parserResultPattern.expression.status.errors.length &&
      parserResultPattern.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parserResultPattern]
  )

  const [parserResultPatternBasic, setParserResultPatternBasic] = useState({})

  useEffect(() => {
    try {
      const tree = parserResultPatternError
        ? null
        : 0 < item.pattern.trim().length
        ? 0 < parserResultPattern.expression.status.errors.length
          ? null
          : checkPattern
          ? parserResultPattern &&
            parserResultPatternPrev &&
            isEqual(parserResultPattern.expression.status, parserResultPatternPrev.expression.status)
            ? parserResultPatternBasic
            : basicFilters.convertToBasic(SchemaRules.flatten(parserResultPattern.expression.tree)) || null
          : {}
        : {}
      setParserResultPatternBasic(null === tree || 1 < Object.keys(tree).length ? null : tree)
    } catch {
      setParserResultPatternBasic(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkPattern, basicFilters, parserResultPattern, parserResultPatternError])

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: data.windowUID,
      remove: () =>
        dispatch({
          type: 'DELETE_ANSWER_VARIANT',
          data: position - 1
        })
    }
  }, [item, position, id, data.windowUID, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-answer-${data.windowUID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-answer-${data.windowUID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const buttonLabelTemplate = event => {
    const text =
      1 < selectedOption.options.length &&
      selectedOption.options.length == event.value.length &&
      ('multistatemultiselect' !== selectedOption.type || !event.value.some(item => !item.state))
        ? 'All'
        : 0 < event.value.length
        ? 1 == event.value.length && ('multistatemultiselect' !== selectedOption.type || event.value[0].state)
          ? selectedOption.options.find(
              o => o.key == ('multistatemultiselect' == selectedOption.type ? event.value[0].value : event.value[0])
            ).text
          : `Filtered to ${event.value.length}`
        : 'Select...'
    return <span dangerouslySetInnerHTML={{ __html: text }}></span>
  }

  const _onModeChange = (mode, applyToAll = false) => {
    if (applyToAll)
      dispatch({
        type: 'UPDATE_ANSWER_VARIANTS_MODE',
        data: {
          positions: Object.keys(parserResultPatternBasicsRef.current).filter(
            k => null !== parserResultPatternBasicsRef.current[k]
          ),
          mode: mode
        }
      })
    else
      dispatch({
        type: 'UPDATE_ANSWER_VARIANT',
        data: {
          item: {
            ...item,
            mode: mode || ('basic' == item.mode ? 'advanced' : 'basic')
          },
          position: position - 1
        }
      })
  }
  const onModeChange = (mode, applyToAll = false) => {
    if ('advanced' == item.mode && false == item.check) {
      setCheckPattern(true)
    } else _onModeChange(mode, applyToAll)
  }

  const onResetValues = () => {
    setSelectedValue(null)
    setSelectedValues([])
    setSelectedOption(null)

    if ('advanced' != item.mode) {
      onModeChange('advanced')
    }
  }

  const onSelectedChange = (value = null, _values) => {
    const group = data.options.find(v => v.items.some(v => v.key == value)) || null
    if (null !== group && group.disabled) return

    const option = group.items.find(v => v.key == value) || null
    if (null !== option && option.disabled) return

    const values = _values || []
    values.sort((a, b) =>
      'multistatemultiselect' == option.type
        ? option.options.findIndex(v => v.key == a.value) - option.options.findIndex(v => v.key == b.value)
        : option.options.findIndex(v => v.key == a) - option.options.findIndex(v => v.key == b)
    )

    setSelectedValue(value)
    setSelectedValues(values)
    setSelectedOption(option)

    changedRef.current = true

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          pattern:
            null === option
              ? ''
              : basicFilters.convertToAdvanced(
                  convertValuesToTree(
                    option,
                    /^TAG.P?Q\d+(?:SQ\d+)?./.test(value) ? value.match(/^(TAG.P?Q\d+(?:SQ\d+)?)./)[1] : value,
                    values
                  )
                )
        },
        position: position - 1
      }
    })
  }

  const onPatternChange = value => {
    setProcessing(true)

    onResetValues()

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: { ...item, pattern: value },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ANSWER_VARIANT',
      data: position - 1,
      variant: 'oex'
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  })

  const onChange = event =>
    'dropdown' == selectedOption.type
      ? onSelectedChange(selectedValue, [event.value])
      : onSelectedChange(selectedValue, event.value)

  useEffect(() => {
    null !== selectedOption && (dropdownsRef.current[position] = selectedOption.key)

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[position]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, selectedOption]) // TODO

  useEffect(() => {
    if (!changedRef.current) {
      const keys = Object.keys(parserResultPatternBasic || {})

      if (0 < keys.length) {
        const options = data.options.flatMap(v => v.items)
        const option = /^TAG.P?Q\d+(?:SQ\d+)?$/.test(keys[0])
          ? options.find(
              v =>
                String(v.value || v.key)
                  .toLowerCase()
                  .startsWith(`${keys[0].toLowerCase()}.`) &&
                v.options.some(o =>
                  Array.isArray(parserResultPatternBasic[keys[0]].v)
                    ? parserResultPatternBasic[keys[0]].v.some(v => o.key.toLowerCase() == v.toLowerCase())
                    : o.key.toLowerCase() == parserResultPatternBasic[keys[0]].v.toLowerCase()
                )
            ) || null
          : options.find(v => String(v.value || v.key).toLowerCase() == keys[0].toLowerCase()) || null

        parserResultPatternBasicsRef.current[position] = null !== option ? parserResultPatternBasic : null
      } else parserResultPatternBasicsRef.current[position] = 0 < item.pattern.trim().length ? null : {}
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete parserResultPatternBasicsRef.current[position]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.pattern, position, parserResultPatternBasic])

  useEffect(() => {
    if (changedRef.current) {
      setProcessing(false)

      changedRef.current = false
      return
    }

    if (null === parserResultPatternBasic) {
      onResetValues()
    } else {
      const keys = Object.keys(parserResultPatternBasic || {})

      if (0 < keys.length) {
        const options = data.options.flatMap(v => v.items)
        const option = /^TAG.P?Q\d+(?:SQ\d+)?$/.test(keys[0])
          ? options.find(
              v =>
                String(v.value || v.key)
                  .toLowerCase()
                  .startsWith(`${keys[0].toLowerCase()}.`) &&
                v.options.some(o =>
                  Array.isArray(parserResultPatternBasic[keys[0]].v)
                    ? parserResultPatternBasic[keys[0]].v.some(v => o.key.toLowerCase() == v.toLowerCase())
                    : o.key.toLowerCase() == parserResultPatternBasic[keys[0]].v.toLowerCase()
                )
            ) || null
          : options.find(v => String(v.value || v.key).toLowerCase() == keys[0].toLowerCase()) || null

        if (null === option) {
          onResetValues()
        } else {
          const tree = Object.values(parserResultPatternBasic)[0]

          const values = convertTreeToValues(option, tree)

          values.sort((a, b) =>
            'multistatemultiselect' == option.type
              ? option.options.findIndex(v => v.key == a.value) - option.options.findIndex(v => v.key == b.value)
              : option.options.findIndex(v => v.key == a) - option.options.findIndex(v => v.key == b)
          )

          setSelectedValue(option.key)
          setSelectedValues(values)
          setSelectedOption(option)
        }
      }
    }

    setProcessing(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPatternBasic])

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.pattern`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultPattern, parserResultPatternError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.pattern`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultPattern, parserResultPatternError])
  useEffect(() => {
    const patternRefs = getRefs(parserResultPattern)
    const clustersRefs = getClustersRefs(data.modelClusters, parserResultPattern)

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `a${position}`,
        refs: [patternRefs, [], clustersRefs]
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_REFS',
        data: {
          id: `a${position}`,
          refs: null
        }
      })
    }
  }, [position, data.modelClusters, dispatch, parserResultPattern])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `a${position}`,
        error: !item.pattern.trim().length || parserResultPatternError
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_ERRORS',
        data: {
          id: `a${position}`,
          error: null
        }
      })
    }
  }, [item, position, dispatch, parserResultPatternError])

  useEffect(
    () =>
      null !== selectedValue &&
      setSelectedOption(data.options.flatMap(v => v.items).find(v => v.key == selectedValue) || null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.options]
  )

  useEffect(
    () => item.mode !== itemModeRef.current && onModeChange(itemModeRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.mode]
  )

  useEffect(() => setCheckPattern('advanced' != itemMode || false != item.check), [item.check, itemMode])

  const renderOptions = () => (
    <AnswerVariantOptions
      position={position}
      value={selectedValue}
      options={data.options}
      dropdownsRef={dropdownsRef}
      onSelectedChange={onSelectedChange}
    />
  )
  const renderValues = () => {
    if (null === selectedOption || 'none' == selectedOption.type) {
      return <MultiSelect className="p-composite" placeholder="Select..." disabled={true} />
    } else {
      switch (selectedOption.type) {
        case 'multistatemultiselect':
          return (
            <MultiStateMultiSelect
              className="p-composite"
              placeholder="Select..."
              value={selectedValues}
              options={selectedOption.options}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              buttonLabelTemplate={buttonLabelTemplate}
              panelHeaderTemplate={MultiSelectHeader}
              panelFooterTemplate={MultiSelectFooter}
              filter={true}
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide={true}
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onChange}
            />
          )
        case 'multiselect': {
          return (
            <MultiSelect
              className="p-composite"
              placeholder="Select..."
              value={selectedValues}
              options={selectedOption.options}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              buttonLabelTemplate={buttonLabelTemplate}
              panelHeaderTemplate={MultiSelectHeader}
              panelFooterTemplate={MultiSelectFooter}
              filter={true}
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide={true}
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onChange}
            />
          )
        }
        case 'dropdown': {
          return (
            <Dropdown
              className="p-composite"
              placeholder="Select..."
              value={selectedValues[0]}
              options={selectedOption.options}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              filter={true}
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide={true}
              filterInputAutoFocus={false}
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onChange}
            />
          )
        }
        default: {
          return <MultiSelect className="p-composite" placeholder="Select..." disabled={true} />
        }
      }
    }
  }

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']} p-fluid p-col-12`}>
      {disabled ? null : (
        <span className={style.delete} onClick={onDeleteClick}>
          <i className="icon-aytmfd-delete" />
        </span>
      )}
      <div className={`${style.index} p-field`}>Q{position}.</div>
      <div
        className={`${style.group} ${style['group-av']}${
          disabled ? ' ' + style['group-av-disabled'] : ''
        } p-fluid p-field p-col`}
      >
        <div className={`${style.group} p-fluid p-field p-col-${(1 < answerVariantAmount && 8) || 12}`}>
          <ModeToggle
            mode={itemMode}
            processing={processing}
            toggleDisabled={
              null === parserResultPatternBasic ||
              (0 < Object.keys(parserResultPatternBasic).length && null === selectedOption)
            }
            onModeChange={onModeChange}
          />
          {'advanced' == itemMode ? (
            <div className="p-field p-col-12">
              <AdvancedFilterContainer
                className="p-component p-inputtext"
                pattern={item.pattern}
                parser={data.parser}
                locale={data.locale}
                surveySchema={data.surveySchema}
                clusters={data.clusters}
                parserResultEof={parserResultEof}
                hintPosition="top"
                disabled={disabled}
                onChange={disabled ? null : onPatternChange}
              />
            </div>
          ) : (
            <>
              <div className="p-field p-col-6">{renderOptions()}</div>
              <div className="p-field p-col-6">{renderValues()}</div>
            </>
          )}
        </div>
        {1 < answerVariantAmount ? (
          <div className="p-fluid p-field p-col">
            <InputText
              placeholder="Please type a label..."
              value={decodeText(item.text)}
              maxLength={120}
              disabled={disabled}
              onChange={disabled ? null : onLabelChange}
            />
          </div>
        ) : null}
      </div>
      {disabled ? null : (
        <div className="p-field">
          <span ref={dndListItemHandleRef} className={style.handle} onClick={onHandleClick}>
            <i className="icon-aytmfd-handle" />
          </span>
        </div>
      )}
    </div>
  )
}

AnswerVariantOEX.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dndItemsList: PropTypes.any
}

AnswerVariantOEX.displayName = 'AnswerVariantOEX'

export default AnswerVariantOEX
